import { EyeOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import type { RootState } from "../../../store.ts"
import mergeObjects from "../../../utils/object/merge-objects.ts"
import { showDocumentValidationError } from "../helpers/show-document-validation-error.ts"
import {
  createProposalDocument,
  saveProposalDocument,
  sendProposalDocumentToApproval
} from "../redux/thunks/document-proposal.thunks.ts"
import {
  fetchDocumentInitialData,
  fetchDocumentPreview,
  fetchTemplateById
} from "../redux/thunks/document-templates.thunks.ts"
import { type DocumentRequestData } from "../types/document.types.ts"
import { type TemplateDocumentFormData } from "../types/template-document-form-data.tsx"
import type { DocumentInitialData, DocumentTemplate } from "../types/types.ts"
import { DocumentBackToLeadBtn } from "./components/DocumentBackToLeadBtn.tsx"
import { TemplateDocumentForm } from "./components/TemplateDocumentForm/TemplateDocumentForm.tsx"
import { TemplateDocumentPreview } from "./components/TemplateDocumentPreview.tsx"

const { Title, Text } = Typography

function DocumentProposalCreatePage({
  getInitialData,
  getTemplate,
  createDocument,
  saveDocument,
  sendDocumentToApproval,
  fetchDocumentPreview
}: ConnectedProps<typeof connector>) {
  const [form] = Form.useForm<TemplateDocumentFormData>()

  const { normalizeDate } = useDateFormat()
  const navigate = useNavigate()
  const params = useParams<{ templateId: string; leadId: string }>()

  const templateId = params?.templateId as string
  const leadId = params?.leadId as string

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [template, setTemplate] = useState<DocumentTemplate | null>(null)
  const [initialData, setInitialData] = useState<DocumentInitialData | null>(
    null
  )

  const handleFetchDocumentPreview = useCallback(
    async (data: DocumentRequestData) => {
      return fetchDocumentPreview(data).unwrap()
    },
    [fetchDocumentPreview]
  )

  const handleCreateDocument = useCallback(async () => {
    try {
      const values = await form.validateFields()

      return createDocument({
        lead_id: initialData?.lead_id,
        customer_id: initialData?.customer_id,
        template_id: initialData?.template_id,
        ...values,
        completion_date: normalizeDate(values?.completion_date),
        start_date: normalizeDate(values?.start_date)
      }).unwrap()
    } catch (e) {
      showDocumentValidationError(form, e)
      console.log(e)
    }
  }, [
    createDocument,
    form,
    initialData?.blocks,
    initialData?.customer_id,
    initialData?.lead_id,
    initialData?.template_id
  ])

  // save draft without pdf
  const handleSaveDraft = useCallback(async () => {
    try {
      setSaving(true)
      const createdDocument = await handleCreateDocument()
      const leadId = createdDocument?.lead_id
      if (!leadId) throw new Error("Lead id is required")

      navigate(RoutesPaths.lead.view(leadId))
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [handleCreateDocument, navigate])

  // save pdf
  const handleSaveDocument = useCallback(async () => {
    try {
      setSaving(true)

      const createdDocument = await handleCreateDocument()
      const id = createdDocument?.id
      if (!id) throw new Error("Document id is required")

      await saveDocument(id)
      const leadId = createdDocument?.lead_id
      if (!leadId) throw new Error("Lead id is required")
      navigate(RoutesPaths.lead.view(leadId))
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [handleCreateDocument, navigate, saveDocument])

  const handleSendToApproval = useCallback(async () => {
    try {
      setSaving(true)

      const createdDocument = await handleCreateDocument()
      const id = createdDocument?.id
      if (!id) throw new Error("Document id is required")

      await sendDocumentToApproval(id).unwrap()

      const leadId = createdDocument?.lead_id
      if (!leadId) throw new Error("Lead id is required")
      navigate(RoutesPaths.lead.view(leadId))
    } catch (e) {
      console.log(e)
    } finally {
      setSaving(false)
    }
  }, [handleCreateDocument, navigate, sendDocumentToApproval])

  const handleGetAllData = useCallback(async () => {
    if (!leadId || !templateId) {
      return console.error(
        "Lead id and template id are required to fetch initial data"
      )
    }

    try {
      const initDataRes = await getInitialData({ templateId, leadId }).unwrap()
      if (initDataRes) setInitialData(initDataRes)

      const templateRes = await getTemplate(templateId).unwrap()
      if (templateRes) setTemplate(templateRes)
    } finally {
      setLoading(false)
    }
  }, [getInitialData, getTemplate, leadId, templateId])

  const handleSubmit = useCallback(
    async (data: TemplateDocumentFormData) => {
      try {
        setSaving(true)
        await handleSaveDocument()
      } catch (e) {
        console.log()
      } finally {
        setSaving(false)
      }
    },
    [handleSaveDocument]
  )

  const mergedInitialValues = useMemo(() => {
    if (!initialData) return undefined
    if (!template) return initialData

    const mergedData = mergeObjects(initialData, template, {
      base: "first",
      mergeExtra: true
    })

    return { ...(mergedData || {}), phases: initialData?.phases || [] }
  }, [initialData, template])

  useEffect(() => {
    void handleGetAllData()
    generateDocumentTitle("Create Document")
  }, [handleGetAllData])

  if (!leadId) return <div>Lead id is required</div>
  if (loading) return <Skeleton.Node active className={"size-full"} />
  if (!template) return <div>Template not found</div>

  return (
    <FixedContainer>
      <Flex vertical gap={"small"} className={"overflow-hidden"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex vertical>
            <Title level={4}>Create Document</Title>
            <Text type="secondary">Template: {template?.name}</Text>
          </Flex>
          <Space wrap>
            {leadId && (
              <DocumentBackToLeadBtn leadId={leadId} saving={saving} />
            )}
            <TemplateDocumentPreview
              saving={saving}
              form={form}
              templateId={initialData?.template_id}
              leadId={initialData?.lead_id}
              customerId={initialData?.customer_id}
              fetchDocumentPreview={handleFetchDocumentPreview}
              submitLabel={"Save as Draft"}
              onSubmitClick={handleSaveDraft}
              onGenerateClick={handleSaveDocument}
              onClickSendToApproval={handleSendToApproval}
            />

            <Button
              loading={saving}
              onClick={form.submit}
              variant={"solid"}
              color={"primary"}
              icon={<EyeOutlined />}
            >
              Save & Generate
            </Button>
          </Space>
        </Flex>
        <TemplateDocumentForm
          form={form}
          saving={saving}
          initialValues={mergedInitialValues}
          projectTypes={initialData?.project_type_details || []}
          // initialValues={{ ...(initialData || {}), ...FULL_MOCK } as any}
          onSubmit={handleSubmit}
        />
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = (state: RootState) => ({})

const mapDispatchToProps = {
  // getBlocks: fetchAvailableBlocks,
  getInitialData: fetchDocumentInitialData,
  getTemplate: fetchTemplateById,
  createDocument: createProposalDocument,
  saveDocument: saveProposalDocument,
  sendDocumentToApproval: sendProposalDocumentToApproval,
  fetchDocumentPreview
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentProposalCreatePage)
